<!-- src/lib/components/FileExplorer.svelte -->
<!--
  VS Code-style collapsible file tree. Lives in a left-side pane that sits
  between ProjectSidebar and .main. Toggled with Cmd+B / Ctrl+B from App.svelte.

  Tree model:
    - `tree[path]` holds the children of a directory, or `null` if not yet loaded.
    - `expanded` is a Set of directory paths that should render their children.
    - Expanding a collapsed dir kicks off a lazy `loadDir(path)` fetch.
    - There is no watcher; the user can manually "refresh root" via the header
      button when the filesystem changes underneath them.

  Root picker:
    - Tauri: uses the `select_directory` invoke that already exists in
      src-tauri/src/lib.rs (native folder picker).
    - Browser/dev: falls back to a text prompt asking for an absolute path.
-->
<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '../api.ts';
  import { platform } from '../platform.ts';

  interface FileEntry {
    name:     string;
    path:     string;
    is_dir:   boolean;
    size:     number | null;
    modified: string | null;
  }

  interface Props {
    /** Optional preset root. When null, shows an "Open folder…" empty state. */
    rootPath?: string | null;
    onSelectFile?: (path: string) => void;
    /** Called when the user picks a new root — parent should hoist it into
     *  shared state so other panels (Git) see the same path. */
    onRootChange?: (path: string) => void;
  }
  let { rootPath = null, onSelectFile, onRootChange }: Props = $props();

  // Local mutable copy of the root so the picker can update it without
  // needing a parent callback. Initialized to null and populated from the
  // `rootPath` prop via the effect below — that way changes to the prop
  // after mount (e.g. from a card-linked worktree) still propagate.
  let currentRoot = $state<string | null>(null);

  $effect(() => {
    if (rootPath && rootPath !== currentRoot) {
      currentRoot = rootPath;
    }
  });

  // Tree state: map path → children list (null = not loaded)
  let tree = $state<Record<string, FileEntry[] | null>>({});
  let expanded = $state<Set<string>>(new Set());
  let loading  = $state<Set<string>>(new Set());
  let errorPaths = $state<Record<string, string>>({});

  async function loadDir(path: string, force = false) {
    if (!force && tree[path] !== undefined) return;
    loading.add(path);
    loading = new Set(loading);
    try {
      const entries = await api<FileEntry[]>(`/api/v1/files/list?path=${encodeURIComponent(path)}`);
      tree[path] = entries;
      delete errorPaths[path];
      errorPaths = { ...errorPaths };
    } catch (e: unknown) {
      tree[path] = [];
      errorPaths[path] = e instanceof Error ? e.message : 'Failed to read directory';
      errorPaths = { ...errorPaths };
    } finally {
      loading.delete(path);
      loading = new Set(loading);
    }
  }

  function toggleFolder(entry: FileEntry) {
    if (!entry.is_dir) {
      // File click: bubble up to the parent
      onSelectFile?.(entry.path);
      return;
    }
    if (expanded.has(entry.path)) {
      expanded.delete(entry.path);
    } else {
      expanded.add(entry.path);
      void loadDir(entry.path);
    }
    expanded = new Set(expanded);
  }

  // Load the root when it first appears
  $effect(() => {
    const root = currentRoot;
    if (!root) return;
    const alreadyLoaded = untrack(() => tree[root] !== undefined);
    if (!alreadyLoaded) {
      void loadDir(root);
    }
  });

  /// Folder picker. Routes through lib/platform which handles both the
  /// Tauri desktop shell (native folder dialog) and browser dev mode
  /// (returns null, so we fall back to a prompt). The naive "try to
  /// import @tauri-apps/api" pattern HANGS THE BROWSER when there's no
  /// Tauri host, because invoke() never resolves. lib/platform.ts exists
  /// specifically to fix that — it checks for the __TAURI_INTERNALS__
  /// runtime global first.
  async function pickRoot() {
    let path: string | null = await platform.selectDirectory();
    if (path == null) {
      // Not Tauri (or user cancelled) — fall back to a text prompt.
      const entered = window.prompt(
        'Enter an absolute path to a folder:',
        currentRoot ?? ''
      );
      path = entered && entered.trim() ? entered.trim() : null;
    }
    if (path) {
      currentRoot = path;
      tree = {};
      expanded = new Set();
      errorPaths = {};
      void loadDir(path);
      onRootChange?.(path);
    }
  }

  async function refreshRoot() {
    if (!currentRoot) return;
    // Reload every already-expanded directory (including the root)
    const pathsToReload = [currentRoot, ...Array.from(expanded)];
    for (const p of pathsToReload) {
      // eslint-disable-next-line no-await-in-loop
      await loadDir(p, true);
    }
  }

  // Derived: a flat, ordered list of visible rows so we don't need a
  // recursive component. Each row has a depth for indentation.
  interface Row {
    entry: FileEntry;
    depth: number;
  }

  function buildVisibleRows(root: string | null): Row[] {
    if (!root) return [];
    const out: Row[] = [];
    const walk = (path: string, depth: number) => {
      const children = tree[path];
      if (!children) return;
      for (const e of children) {
        out.push({ entry: e, depth });
        if (e.is_dir && expanded.has(e.path)) {
          walk(e.path, depth + 1);
        }
      }
    };
    walk(root, 0);
    return out;
  }

  const visibleRows: Row[] = $derived(buildVisibleRows(currentRoot));

  // Icons — purely decorative, one glyph per file "kind"
  function iconFor(entry: FileEntry): string {
    if (entry.is_dir) return expanded.has(entry.path) ? '▾' : '▸';
    const ext = entry.name.split('.').pop()?.toLowerCase() ?? '';
    const map: Record<string, string> = {
      ts: '◆', tsx: '◆', js: '◇', jsx: '◇', mjs: '◇', cjs: '◇',
      rs: '◉', py: '◎', go: '◈', java: '◈', kt: '◈', rb: '◈',
      md: '≡', markdown: '≡', mdx: '≡',
      json: '{}', toml: '⊡', yaml: '⊟', yml: '⊟',
      svelte: '◊', vue: '◊', html: '⌘', htm: '⌘',
      css: '▣', scss: '▣', sass: '▣', less: '▣',
      png: '▥', jpg: '▥', jpeg: '▥', gif: '▥', webp: '▥', svg: '▥',
      sh: '>', zsh: '>', bash: '>', fish: '>',
      sql: '⊞',
      lock: '⊕',
    };
    return map[ext] ?? '·';
  }

  // Short path display for the header: ~/Code/agentboard instead of
  // /Users/cashconway/Code/agentboard
  function shortPath(p: string): string {
    // Try to tilde-collapse $HOME
    const m = p.match(/^\/Users\/([^/]+)(\/.*)?$/);
    if (m) return '~' + (m[2] ?? '');
    return p;
  }
</script>

<div class="file-explorer">
  <div class="file-explorer-header">
    <span class="header-label">Files</span>
    <div class="header-actions">
      {#if currentRoot}
        <button
          class="icon-btn"
          title="Refresh"
          onclick={refreshRoot}
        >↻</button>
        <button
          class="icon-btn"
          title="Open a different folder"
          onclick={pickRoot}
        >…</button>
      {/if}
    </div>
  </div>

  {#if currentRoot}
    <div class="current-root" title={currentRoot}>{shortPath(currentRoot)}</div>
  {/if}

  <div class="file-tree">
    {#if !currentRoot}
      <div class="empty-state">
        <p class="empty-title">No folder open</p>
        <p class="empty-hint">Pick a directory to browse it inline.</p>
        <button class="open-folder-btn" onclick={pickRoot}>Open folder…</button>
      </div>
    {:else if tree[currentRoot] === undefined || (loading.has(currentRoot) && !tree[currentRoot])}
      <div class="loading">Loading…</div>
    {:else if errorPaths[currentRoot]}
      <div class="error-state">
        <p>{errorPaths[currentRoot]}</p>
        <button class="retry-btn" onclick={() => loadDir(currentRoot!, true)}>Retry</button>
      </div>
    {:else if visibleRows.length === 0}
      <div class="empty-state">
        <p class="empty-title">Empty folder</p>
      </div>
    {:else}
      {#each visibleRows as row (row.entry.path)}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="file-row"
          class:is-dir={row.entry.is_dir}
          class:is-file={!row.entry.is_dir}
          style="padding-left: {0.4 + row.depth * 0.9}rem;"
          onclick={() => toggleFolder(row.entry)}
          title={row.entry.path}
        >
          <span class="file-icon" class:folder-icon={row.entry.is_dir}>{iconFor(row.entry)}</span>
          <span class="file-name">{row.entry.name}</span>
          {#if loading.has(row.entry.path)}
            <span class="row-loading">·</span>
          {/if}
        </div>
      {/each}
    {/if}
  </div>
</div>

<style>
  .file-explorer {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    /* Background + border + filament are now owned by the parent
       .file-explorer-pane in App.svelte so Git and Files share them. */
    position: relative;
    overflow: hidden;
    color: oklch(0.80 0.05 310 / 0.85);
    font-size: 0.78rem;
  }

  .file-explorer-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.55rem 0.75rem 0.45rem 0.75rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.12);
  }

  .header-label {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.72 0.08 310 / 0.6);
  }

  .header-actions {
    display: flex;
    gap: 0.2rem;
  }

  .icon-btn {
    background: transparent;
    border: none;
    color: oklch(0.60 0.05 310 / 0.55);
    width: 20px;
    height: 20px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.82rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 120ms ease, color 120ms ease;
  }
  .icon-btn:hover {
    background: oklch(0.72 0.28 310 / 0.14);
    color: oklch(0.90 0.16 310);
  }

  .current-root {
    flex-shrink: 0;
    padding: 0.35rem 0.75rem 0.4rem;
    font-size: 0.68rem;
    color: oklch(0.55 0.05 310 / 0.65);
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.08);
  }

  .file-tree {
    flex: 1;
    overflow-y: auto;
    padding: 0.25rem 0;
  }
  .file-tree::-webkit-scrollbar { width: 8px; }
  .file-tree::-webkit-scrollbar-thumb {
    background: oklch(0.30 0.05 275 / 0.4);
    border-radius: 4px;
  }
  .file-tree::-webkit-scrollbar-thumb:hover {
    background: oklch(0.40 0.10 310 / 0.5);
  }

  .file-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    padding-right: 0.5rem;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    transition: background 80ms ease, color 80ms ease;
    user-select: none;
  }
  .file-row:hover {
    background: oklch(0.72 0.28 310 / 0.07);
    color: oklch(0.95 0.05 310);
  }
  .file-row.is-dir .file-name {
    color: oklch(0.88 0.06 310);
    font-weight: 500;
  }
  .file-row.is-file .file-name {
    color: oklch(0.72 0.03 310 / 0.85);
  }
  .file-row.is-file:hover .file-name {
    color: oklch(0.92 0.04 310);
  }

  .file-icon {
    width: 1rem;
    flex-shrink: 0;
    text-align: center;
    color: oklch(0.55 0.05 310 / 0.5);
    font-size: 0.8rem;
  }
  .file-icon.folder-icon {
    color: oklch(0.76 0.18 85 / 0.85);  /* gold for folders */
  }
  .file-row.is-dir:hover .file-icon.folder-icon {
    color: oklch(0.86 0.17 85);
  }

  .file-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .row-loading {
    font-size: 0.8rem;
    color: oklch(0.80 0.20 310 / 0.6);
    animation: row-pulse 1s ease-in-out infinite;
  }
  @keyframes row-pulse {
    0%, 100% { opacity: 0.3; }
    50%       { opacity: 1; }
  }

  .empty-state,
  .loading,
  .error-state {
    padding: 1.5rem 1rem;
    text-align: center;
    color: oklch(0.55 0.05 310 / 0.55);
    font-size: 0.78rem;
  }
  .empty-title {
    color: oklch(0.75 0.05 310 / 0.75);
    margin: 0 0 0.35rem 0;
    font-size: 0.85rem;
    font-weight: 500;
  }
  .empty-hint {
    margin: 0 0 1rem 0;
    font-size: 0.72rem;
    color: oklch(0.55 0.05 310 / 0.5);
  }
  .open-folder-btn,
  .retry-btn {
    background: oklch(0.72 0.28 310 / 0.10);
    border: 1px solid oklch(0.72 0.28 310 / 0.30);
    color: oklch(0.88 0.18 310);
    padding: 0.35rem 0.9rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 500;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .open-folder-btn:hover,
  .retry-btn:hover {
    background: oklch(0.72 0.28 310 / 0.20);
    border-color: oklch(0.80 0.26 310 / 0.50);
    color: oklch(0.94 0.15 310);
  }

  .error-state p {
    color: oklch(0.72 0.22 25 / 0.85);
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    margin: 0 0 0.75rem 0;
    word-break: break-word;
  }

  @media (prefers-reduced-motion: reduce) {
    .file-explorer::after,
    .row-loading { animation: none; }
  }
</style>
